import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

/**
 * A labelled input.
 *
 * The label is always rendered — placeholder-only fields fail for screen readers and disappear
 * the moment someone starts typing. Errors are associated with `aria-describedby` and announced,
 * so they are not conveyed by red text alone (WCAG 2.2 AA, plan §40).
 */
export function TextField({ label, error, hint, className, ...rest }: TextFieldProps): ReactNode {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="field-group">
      <label className="field-group__label" htmlFor={id}>
        {label}
      </label>
      {hint === undefined ? null : (
        <p className="field-group__hint" id={hintId}>
          {hint}
        </p>
      )}
      <input
        {...rest}
        id={id}
        className={['input', error === undefined ? '' : 'input--invalid', className]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />
      {error === undefined ? null : (
        <p className="field-group__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
